import {
  Button,
  createMuiTheme,
  MuiThemeProvider,
  TextField,
} from "@material-ui/core";
import Autocomplete from "@material-ui/lab/Autocomplete/Autocomplete";
import React, { useEffect, useState } from "react";
import { shallowEqual, useSelector } from "react-redux";
import { useHistory, useLocation, useParams } from "react-router-dom";
import {
  Card,
  CardBody,
  CardHeader,
  CardHeaderToolbar,
} from "../../../../_metronic/_partials/controls";
import {
  deleteProduct,
  getProductById,
  postProduct,
  updateProduct,
} from "../../../../api/product";
import { getProductFamilies } from "../../../../api/productFamily";
import { getProductMarcas } from "../../../../api/productMarca";
import { getProductTypes } from "../../../../api/productType";
import { getProductUnitTypes } from "../../../../api/productUnitType";
import { alertError, alertSuccess } from "../../../../utils/logger";
import ConfirmDialog from "../../../components/dialogs/ConfirmDialog";
import DurationTextField from "../../../components/fields/DurationTextField";
import { useSkeleton } from "../../../hooks/useSkeleton";

// Create theme for delete button (red)
const theme = createMuiTheme({
  palette: {
    secondary: {
      main: "#F64E60",
    },
  },
});

function getEmptyProduct() {
  return {
    referencia: null,
    descripcion: null,
    familia_id: null,
    tipo_producto_id: null,
    precio: null,
    tipo_unidad_id: null,
    tiempo_instalacion: null,
    descuento_base: null,
  };
}

export default function EditProductsPage() {
  const [product, setProduct] = useState(getEmptyProduct());
  const [installationTimeIsValid, setInstallationTimeIsValid] = useState(true);

  const [productFamilies, setProductFamilies] = useState([]);
  const [productMarcas, setProductMarcas] = useState([]);
  const [productTypes, setProductTypes] = useState([]);
  const [productUnitTypes, setProductUnitTypes] = useState([]);

  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);

  const productId = useParams().id;
  const history = useHistory();
  const location = useLocation();

  const loggedUser = useSelector(
    (store) => store.authentication?.user,
    shallowEqual
  );
  const handleBack = () => {
    const prevTablePage = location.state?.page;

    history.push(`/productos${prevTablePage ? "?page=" + prevTablePage : ""}`);
  };

  const {
    isLoading: isLoadingData,
    disableLoading: disableLoadingData,
    ContentSkeleton,
  } = useSkeleton();

  useEffect(() => {
    getProductFamilies(loggedUser?.accessToken)
      .then((res) => {
        if (res.status === 200) {
          setProductFamilies(res.data);
        }
      })
      .catch((error) => {
        alertError({
          error: error,
          customMessage: "No se han podido obtener las familias de productos.",
        });
        handleBack();
      });
    getProductMarcas(loggedUser?.accessToken)
      .then((res) => {
        if (res.status === 200) {
          setProductMarcas(res.data);
        }
      })
      .catch((error) => {
        alertError({
          error: error,
          customMessage: "No se han podido obtener las familias de productos.",
        });
        handleBack();
      });
    getProductTypes(loggedUser?.accessToken)
      .then((res) => {
        if (res.status === 200) {
          setProductTypes(res.data);
        }
      })
      .catch((error) => {
        alertError({
          error: error,
          customMessage: "No se han podido obtener los tipos de productos.",
        });
        handleBack();
      });
    getProductUnitTypes(loggedUser?.accessToken)
      .then((res) => {
        if (res.status === 200) {
          setProductUnitTypes(res.data);
        }
      })
      .catch((error) => {
        alertError({
          error: error,
          customMessage:
            "No se han podido obtener los tipos de unidades de productos.",
        });
        handleBack();
      });
    if (!productId) {
      disableLoadingData();
      return;
    }
    getProductById(productId, loggedUser?.accessToken)
      .then((res) => {
        if (res.status === 200) {
          setProduct({
            ...res.data,
          });

          disableLoadingData();
        }
      })
      .catch((error) => {
        alertError({
          error: error,
          customMessage: "No se ha podido obtener el producto.",
        });
        handleBack();
      });
  }, [productId, disableLoadingData, history]);

  function saveProduct() {
    const validateProduct = (product) => {
      let error = "";

      if (!product?.descripcion) {
        error = "La descripción es obligatoria.";
      } else if (!product?.referencia) {
        error = "La referencia es obligatoria.";
      } else if (!product?.familia_id) {
        error = "La familia es obligatoria.";
      } else if (!product?.tipo_producto_id) {
        error = "El tipo de producto es obligatorio.";
      } else if (!product?.tipo_unidad_id) {
        error = "El tipo de unidad es obligatorio.";
      } else if (product?.precio === null || product?.precio === "") {
        error = "El precio es obligatorio.";
      } else if (!installationTimeIsValid || !product?.tiempo_instalacion) {
        error = "El tiempo de instalación es obligatorio.";
      }

      if (error) {
        alertError({
          error: error,
        });
        return false;
      }

      return true;
    };

    const productToSave = product;

    if (!validateProduct(productToSave)) {
      return;
    }

    if (!productId) {
      postProduct(productToSave, loggedUser?.accessToken)
        .then((res) => {
          if (res.status === 201) {
            alertSuccess({
              title: "¡Guardado!",
              customMessage: "Producto guardado con éxito.",
            });
            handleBack();
          }
        })
        .catch((error) => {
          alertError({
            error: error,
            customMessage: "No se ha podido guardar el producto.",
          });
        });
    } else {
      updateProduct(productId, productToSave, loggedUser?.accessToken)
        .then((res) => {
          if (res.status === 204) {
            alertSuccess({
              title: "¡Guardado!",
              customMessage: "Cambios guardados con éxito.",
            });
            handleBack();
          }
        })
        .catch((error) => {
          alertError({
            error: error,
            customMessage: "No se han podido guardar los cambios.",
          });
        });
    }
  }

  const handleChange = (element) => (event) => {
    setProduct({ ...product, [element]: event.target.value });
  };

  const handleInstallationTimeChange = (seconds) => {
    setInstallationTimeIsValid(seconds !== null);
    if (seconds !== null) {
      setProduct((current) => ({ ...current, tiempo_instalacion: seconds }));
    }
  };

  if (isLoadingData) return <ContentSkeleton />;
  else
    return (
      <>
        <Card>
          <CardHeader title="Producto">
            <CardHeaderToolbar>
              <Button
                onClick={() => saveProduct()}
                variant="outlined"
                color="primary"
                style={{
                  marginRight: productId ? "20px" : "",
                }}
              >
                Guardar producto
              </Button>
              {productId && (
                <>
                  <MuiThemeProvider theme={theme}>
                    <Button
                      className="py-2"
                      onClick={() => setOpenConfirmDialog(true)}
                      variant="outlined"
                      color="secondary"
                    >
                      Eliminar producto
                    </Button>
                  </MuiThemeProvider>
                </>
              )}
            </CardHeaderToolbar>
          </CardHeader>
          <CardBody>
            <div className="row d-flex align-items-center">
              <div className="col-md-6">
                <TextField
                  id={`descripcion`}
                  label="Descripción"
                  value={product.descripcion}
                  onChange={handleChange("descripcion")}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  margin="normal"
                  variant="outlined"
                  required
                />
              </div>
              <div className="col-md-6">
                <TextField
                  id={`referencia`}
                  label="Referencia"
                  value={product.referencia}
                  onChange={handleChange("referencia")}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  margin="normal"
                  variant="outlined"
                  required
                />
              </div>
            </div>
            <div className="row d-flex align-items-center">
              <div className="col-md-3">
                <Autocomplete
                  id="autocomplete-familia"
                  disablePortal
                  disableClearable
                  filterSelectedOptions
                  options={productFamilies}
                  getOptionLabel={(option) => option.descripcion}
                  value={
                    productFamilies?.find(
                      (x) => x.id === product?.familia_id
                    ) || ""
                  }
                  onChange={(event, selected) => {
                    setProduct({
                      ...product,
                      familia_id: selected ? selected.id : null,
                    });
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Familia"
                      margin="normal"
                      variant="outlined"
                      InputLabelProps={{
                        shrink: true,
                      }}
                      required
                    />
                  )}
                />
              </div>
              <div className="col-md-3">
                <Autocomplete
                  id="autocomplete-familia"
                  disablePortal
                  disableClearable
                  filterSelectedOptions
                  options={productMarcas}
                  getOptionLabel={(option) => option.nombre}
                  value={
                    productMarcas?.find((x) => x.id === product?.marca_id) || ""
                  }
                  onChange={(event, selected) => {
                    setProduct({
                      ...product,
                      marca_id: selected ? selected.id : null,
                    });
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Marca"
                      margin="normal"
                      variant="outlined"
                      InputLabelProps={{
                        shrink: true,
                      }}
                      required
                    />
                  )}
                />
              </div>
              <div className="col-md-3">
                <Autocomplete
                  id="autocomplete-tipo"
                  disablePortal
                  disableClearable
                  filterSelectedOptions
                  options={productTypes}
                  getOptionLabel={(option) => option.descripcion}
                  value={
                    productTypes?.find(
                      (x) => x.id === product?.tipo_producto_id
                    ) || ""
                  }
                  onChange={(event, selected) => {
                    setProduct({
                      ...product,
                      tipo_producto_id: selected ? selected.id : null,
                    });
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Tipo de Producto"
                      margin="normal"
                      variant="outlined"
                      InputLabelProps={{
                        shrink: true,
                      }}
                      required
                    />
                  )}
                />
              </div>
              <div className="col-md-3">
                <Autocomplete
                  id="autocomplete-tipo-unidad"
                  disablePortal
                  disableClearable
                  filterSelectedOptions
                  options={productUnitTypes}
                  getOptionLabel={(option) => option.descripcion}
                  value={
                    productUnitTypes?.find(
                      (x) => x.id === product?.tipo_unidad_id
                    ) || ""
                  }
                  onChange={(event, selected) => {
                    setProduct({
                      ...product,
                      tipo_unidad_id: selected ? selected.id : null,
                    });
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Tipo de Unidad"
                      margin="normal"
                      variant="outlined"
                      InputLabelProps={{
                        shrink: true,
                      }}
                      required
                    />
                  )}
                />
              </div>
            </div>
            <div className="row d-flex align-items-center">
              <div className="col-md-4">
                <TextField
                  id={`precio`}
                  label="Precio (€)"
                  value={product.precio}
                  onChange={handleChange("precio")}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  InputProps={{
                    inputProps: {
                      min: 0,
                    },
                  }}
                  type="number"
                  margin="normal"
                  variant="outlined"
                  required
                />
              </div>
              <div className="col-md-4">
                <DurationTextField
                  id={`tiempo_instalacion`}
                  label="Tiempo de instalación (hh:mm:ss)"
                  value={product.tiempo_instalacion}
                  onDurationChange={handleInstallationTimeChange}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  margin="normal"
                  variant="outlined"
                  required
                />
              </div>
              <div className="col-md-4">
                <TextField
                  id={`descuento`}
                  label="Descuento base"
                  value={product.descuento_base}
                  onChange={handleChange("descuento_base")}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  InputProps={{
                    inputProps: {
                      min: 0,
                    },
                  }}
                  type="number"
                  margin="normal"
                  variant="outlined"
                />
              </div>
            </div>
          </CardBody>
        </Card>
        <Button
          onClick={handleBack}
          variant="outlined"
          style={{ marginRight: "20px" }}
        >
          Atrás
        </Button>
        <Button
          onClick={() => saveProduct()}
          variant="outlined"
          color="primary"
          style={{ marginRight: productId ? "20px" : "" }}
        >
          Guardar producto
        </Button>
        {productId && (
          <>
            <MuiThemeProvider theme={theme}>
              <Button
                className="py-2"
                onClick={() => setOpenConfirmDialog(true)}
                variant="outlined"
                color="secondary"
              >
                Eliminar producto
              </Button>
            </MuiThemeProvider>

            <ConfirmDialog
              title={"¿Estás seguro de que quieres eliminar este producto?"}
              open={openConfirmDialog}
              setOpen={setOpenConfirmDialog}
              onConfirm={() => {
                deleteProduct(productId, loggedUser?.accessToken)
                  .then((res) => {
                    if (res.status === 204 || res.status === 200) {
                      alertSuccess({
                        title: "¡Eliminado!",
                        customMessage: "Producto eliminado con éxito",
                      });
                      handleBack();
                    }
                  })
                  .catch((error) => {
                    alertError({
                      error: error,
                      customMessage: "No se ha podido eliminar el producto.",
                    });
                  });
              }}
            />
          </>
        )}
      </>
    );
}
